export type CompressImageOptions = {
  maxDimension?: number;
  quality?: number;
  mimeType?: "image/jpeg" | "image/webp" | "image/png";
};

const DEFAULTS: Required<CompressImageOptions> = {
  maxDimension: 1024,
  quality: 0.82,
  mimeType: "image/jpeg",
};

const loadImage = (file: File | Blob): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

export const compressImage = async (
  file: File,
  options: CompressImageOptions = {}
): Promise<File> => {
  if (!file.type.startsWith("image/")) return file;

  const { maxDimension, quality, mimeType } = { ...DEFAULTS, ...options };

  try {
    const img = await loadImage(file);
    const scale = Math.min(
      1,
      maxDimension / Math.max(img.naturalWidth, img.naturalHeight)
    );
    const width = Math.round(img.naturalWidth * scale);
    const height = Math.round(img.naturalHeight * scale);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, mimeType, quality)
    );
    if (!blob || blob.size >= file.size) return file;

    const ext = mimeType === "image/jpeg" ? "jpg" : mimeType.split("/")[1];
    const baseName = file.name.replace(/\.[^.]+$/, "");
    return new File([blob], `${baseName}.${ext}`, {
      type: mimeType,
      lastModified: Date.now(),
    });
  } catch {
    return file;
  }
};
